import { clamp } from '@deltasierra/utilities-math';
import { lighten } from '@material-ui/core';
import * as React from 'react';
import styled, { useTheme } from 'styled-components';
import { DSColour, getDSColour } from '../../theme/DSColours';

const Wrapper = styled.span`
    position: relative;
    display: inline-flex;
    align-items: center;
    justify-content: center;
`;

interface StyledSvgBackgroundCircleProps {
    $strokeColour: string;
}

const StyledSvgBackgroundCircle = styled.circle<StyledSvgBackgroundCircleProps>`
    stroke: ${props => props.$strokeColour};
`;

interface StyledSvgCircleProps {
    $animationTime: number;
    $strokeColour?: string;
    $offset: number;
}

function offsetToDeg(offset: number): string {
    return `${offset * 360 - 90}deg`;
}

const StyledSvgCircle = styled.circle<StyledSvgCircleProps>`
    stroke: ${props => props.$strokeColour ?? props.theme.palette.primary.main};

    transition: stroke-dashoffset ${props => props.$animationTime}ms;
    transform: rotate(${props => offsetToDeg(props.$offset)});
    transform-origin: 50% 50%;
`;

type CircleProps = React.SVGProps<SVGCircleElement>;

function useColourEnumStroke(colour: DSColour): string {
    const theme = useTheme();
    return getDSColour(colour, theme);
}

function useColourEnumBackground(colour: DSColour | undefined, strokeColour: string): string {
    const theme = useTheme();
    switch (colour) {
        // For custom default to a neutral background
        case undefined:
            return theme.palette.grey[200];
        default:
            return lighten(strokeColour, 0.9);
    }
}

export function calculateCircumference(
    radius: number,
    strokeWidth: number,
): { circumference: number; normalizedRadius: number } {
    const normalizedRadius = radius / 2 - strokeWidth * 2;
    const circumference = normalizedRadius * 2 * Math.PI;
    return { circumference, normalizedRadius };
}

export type PieCirclePartLineProps = {
    /**
     * The base colour for this circle
     */
    colour: DSColour;

    radius: number;

    offsetBetween: number;

    strokeColour?: CircleProps['stroke'];
    strokeWidth: number;
    strokeCap: CircleProps['strokeLinecap'];

    /**
     * Include a background
     */
    background: boolean;
    backgroundColour?: CircleProps['stroke'];

    animateIn: boolean;
    animationInDelay: number;
    /**
     * The animation time for the transition in milliseconds
     */
    animationTime: number;
};

export type PieCirclePart = PieCirclePartLineProps & {
    value: number;
};

export type PieCirclePartProps = PieCirclePart & {
    offset: number;
};

export function PieCirclePart({
    animateIn,
    animationInDelay,
    animationTime,
    background,
    backgroundColour: _backgroundColour,
    colour,
    offset,
    radius,
    strokeCap,
    strokeColour: _strokeColour,
    strokeWidth,
    value: initialProgress,
}: PieCirclePartProps): JSX.Element {
    const [deferredProgress, setProgress] = React.useState(animateIn ? 0 : initialProgress);

    const strokeColourFromEnum = useColourEnumStroke(colour);
    const strokeColour = _strokeColour ?? strokeColourFromEnum;

    const backgroundColourFromEnum = useColourEnumBackground(colour, strokeColour);
    const backgroundColour = _backgroundColour ?? backgroundColourFromEnum;

    const { circumference, normalizedRadius } = calculateCircumference(radius, strokeWidth);

    React.useEffect(() => {
        if (animateIn) {
            setTimeout(
                () => {
                    setProgress(initialProgress);
                },
                Math.max(animationInDelay, 1),
            );
        }
    }, [animateIn, animationInDelay, initialProgress]);

    const progress = clamp(0, 1, deferredProgress);
    const cPos = radius / 2;

    return (
        <Wrapper>
            <svg height={radius} width={radius}>
                {background && (
                    <StyledSvgBackgroundCircle
                        $strokeColour={backgroundColour}
                        cx={cPos}
                        cy={cPos}
                        fill="transparent"
                        r={normalizedRadius}
                        strokeWidth={strokeWidth}
                    />
                )}
                <StyledSvgCircle
                    $animationTime={animationTime}
                    $offset={offset}
                    $strokeColour={strokeColour}
                    cx={cPos}
                    cy={cPos}
                    fill="transparent"
                    r={normalizedRadius}
                    strokeDasharray={`${circumference} ${circumference}`}
                    strokeDashoffset={circumference - progress * circumference}
                    strokeLinecap={strokeCap}
                    strokeWidth={strokeWidth}
                />
            </svg>
        </Wrapper>
    );
}
PieCirclePart.displayName = 'PieCirclePart';
